/**
 * Expense Service
 * API calls for expense management
 */

import api from './api.js';
import type { ApiResponse, PaginatedResponse } from '@/types/api.js';
import type {
  Expense,
  ExpenseWithDetails,
  ExpenseCreateInput,
  ExpenseUpdateInput,
  ExpenseListParams,
  ExpenseStatistics,
  ExpenseCategory,
  Vendor,
} from '@/types/expense.js';

export const expensesService = {
  /**
   * Get paginated list of expenses
   */
  async list(params: ExpenseListParams = {}): Promise<PaginatedResponse<Expense>> {
    const response = await api.get<ApiResponse<PaginatedResponse<Expense>>>('/expenses', {
      params,
    });
    return response.data.data;
  },

  /**
   * Get expense by ID
   */
  async getById(id: string): Promise<ExpenseWithDetails> {
    const response = await api.get<ApiResponse<ExpenseWithDetails>>(`/expenses/${id}`);
    return response.data.data;
  },

  /**
   * Create new expense
   */
  async create(data: ExpenseCreateInput): Promise<ExpenseWithDetails> {
    const response = await api.post<ApiResponse<ExpenseWithDetails>>('/expenses', data);
    return response.data.data;
  },

  /**
   * Update expense
   */
  async update(id: string, data: ExpenseUpdateInput): Promise<ExpenseWithDetails> {
    const response = await api.put<ApiResponse<ExpenseWithDetails>>(`/expenses/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete expense
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/expenses/${id}`);
  },

  /**
   * Approve expense
   */
  async approve(id: string): Promise<Expense> {
    const response = await api.post<ApiResponse<Expense>>(`/expenses/${id}/approve`);
    return response.data.data;
  },

  /**
   * Reject expense
   */
  async reject(id: string, reason?: string): Promise<Expense> {
    const response = await api.post<ApiResponse<Expense>>(`/expenses/${id}/reject`, {
      reason,
    });
    return response.data.data;
  },

  /**
   * Record payment for expense
   */
  async recordPayment(
    id: string,
    data: { amount: number; paymentDate?: string; method?: string; reference?: string }
  ): Promise<Expense> {
    const response = await api.post<ApiResponse<Expense>>(`/expenses/${id}/payments`, data);
    return response.data.data;
  },

  /**
   * Upload attachment
   */
  async uploadAttachment(id: string, file: File): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);
    await api.post(`/expenses/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Delete attachment
   */
  async deleteAttachment(expenseId: string, attachmentId: string): Promise<void> {
    await api.delete(`/expenses/${expenseId}/attachments/${attachmentId}`);
  },

  /**
   * Get expense statistics
   */
  async getStatistics(): Promise<ExpenseStatistics> {
    const response = await api.get<ApiResponse<ExpenseStatistics>>('/expenses/statistics');
    return response.data.data;
  },

  // Categories

  /**
   * Get expense categories
   */
  async getCategories(): Promise<ExpenseCategory[]> {
    const response = await api.get<ApiResponse<ExpenseCategory[]>>('/expenses/categories');
    return response.data.data;
  },

  /**
   * Create expense category
   */
  async createCategory(data: { name: string; type: string; description?: string }): Promise<ExpenseCategory> {
    const response = await api.post<ApiResponse<ExpenseCategory>>('/expenses/categories', data);
    return response.data.data;
  },

  // Vendors

  /**
   * Get vendors
   */
  async getVendors(): Promise<Vendor[]> {
    const response = await api.get<ApiResponse<Vendor[]>>('/expenses/vendors');
    return response.data.data;
  },

  /**
   * Get vendor by ID
   */
  async getVendorById(id: string): Promise<Vendor> {
    const response = await api.get<ApiResponse<Vendor>>(`/expenses/vendors/${id}`);
    return response.data.data;
  },

  /**
   * Create vendor
   */
  async createVendor(data: { name: string; email?: string; phone?: string }): Promise<Vendor> {
    const response = await api.post<ApiResponse<Vendor>>('/expenses/vendors', data);
    return response.data.data;
  },

  /**
   * Update vendor
   */
  async updateVendor(id: string, data: Partial<Vendor>): Promise<Vendor> {
    const response = await api.put<ApiResponse<Vendor>>(`/expenses/vendors/${id}`, data);
    return response.data.data;
  },
};

export default expensesService;
